import * as React from 'react';

import { routes } from 'defs';
import i18n from 'helper/i18n';
import { mapToFrontendType } from 'store/programItems';

import { createStaticIcon } from 'components/atoms/Icon';
import Loader from 'components/atoms/Loader';
import { getRessourcesForCluster } from 'components/views/FavoritesView/ressource';

import FavoriteContent from 'components/molecules/Favorite/Content';
import FavoriteHeader from 'components/molecules/Favorite/Header';
import FavoriteList from 'components/molecules/Favorite/List';
import FavoriteTeaser from 'components/molecules/Favorite/Teaser';
import { FavoritesClusterIDs } from 'components/views/FavoritesView/ressource';
import { FavoriteClusterRessource } from 'components/views/FavoritesView/ressource';
import { FavoritesViewContainerPropsType } from 'containers/views/FavoritesView';
import { FavoriteType } from 'types/Favorites';

import { HistoryType } from 'types/withRouter';

import withVirtualPageView from 'components/hoc/withVirtualPageView';
import { WithVirtualPageView } from 'components/hoc/withVirtualPageView';

import { ImageProps } from 'components/atoms/Image/Image';

export type DynamicProductFavoriteType = {
	productId: number;
	externalId: string;
	contentType: 'PRODUCT_DYNAMIC';
};

type FavoritesProps = WithVirtualPageView &
	FavoritesViewContainerPropsType & {
		favorites: Array<FavoriteType>;
		doLoadFavorites: () => void;
		total: number;
		totalFavoritesLength: number;
		history: HistoryType;
		dynamicProducts: Array<DynamicProductFavoriteType>;
		totalWithDynamicProducts: number;
	};

type FavoritesState = {};

export class FavoritesView extends React.Component<FavoritesProps, FavoritesState> {
	public static displayName = 'FavoritesView';

	public lastTrackedId: string | void = 'INITIAL';

	public componentDidMount() {
		this.props.doLoadFavorites();
	}

	get cluster(): string {
		return this.props.match ? this.props.match.params.cluster || '' : '';
	}

	get clusterRessources(): FavoriteClusterRessource {
		return getRessourcesForCluster(this.cluster);
	}

	get shouldShowDynamicProducts(): boolean {
		const clusterData = this.clusterRessources;

		if (!~[void 0, FavoritesClusterIDs.RECOMMENDATION].indexOf(clusterData.id)) {
			return false;
		}

		return true;
	}

	public shouldShowEmptyMessage(id?: string): boolean {
		const { favorites, dynamicProducts } = this.props;
		const countRecommendations =
			dynamicProducts.length + favorites.filter((fav) => (fav.contentType as any) === 'PRODUCT').length;

		if (id === undefined && favorites.length === 0 && dynamicProducts.length === 0) {
			return true;
		}

		if (id === undefined && (favorites.length > 0 || dynamicProducts.length > 0)) {
			return false;
		}

		if (id === FavoritesClusterIDs.RECOMMENDATION && countRecommendations === 0) {
			return true;
		}

		if (favorites.length === 0 && dynamicProducts.length === 0) {
			return true;
		}

		return false;
	}

	public render() {
		const { total, favorites, dynamicProducts, totalFavoritesLength, trackVirtualPageView } = this.props;
		const clusterData = this.clusterRessources;
		const { id } = clusterData;
		const modifyHeader: {
			modifier?: string;
		} = {};

		if (total < 0) {
			return <Loader />;
		}

		if (totalFavoritesLength === 0) {
			this.props.history.push(routes.profile.indexRoute.href);
		}

		if (favorites.length <= 0) {
			modifyHeader.modifier = 'gap';
		}

		if (this.lastTrackedId !== id) {
			trackVirtualPageView({ cluster: id }, 1, false);
			this.lastTrackedId = id;
		}

		return (
			<div data-cluster-id={id}>
				<FavoriteHeader
					{...modifyHeader}
					preselectedCluster={id}
					title={i18n('react.impulsCoach.views.favorites.title')}
				/>
				{this.shouldShowEmptyMessage(id) && (
					<FavoriteContent
						title={i18n('react.impulsCoach.views.favorites.cluster.empty.title')}
						text={i18n('react.impulsCoach.views.favorites.cluster.empty.text', {
							__disableHtmlEncoding: true,
							icon: `<span class="o-profile__text-icon">${createStaticIcon('heart')}</span>`,
						})}
						icon="heart"
					/>
				)}
				{favorites.length > 0 && (
					<div className="container">
						<div className="row">
							{favorites.map((favorite: FavoriteType) => {
								return (
									<div
										key={`${favorite.contentType}-${favorite.externalId}`}
										className="col-xs-12 col-sm-12 col-md-4"
									>
										<FavoriteTeaser
											{...favorite}
											image={
												favorite.previewImage || favorite.fallbackImage || ({} as ImageProps)
											}
											frontendContentType={mapToFrontendType(favorite.contentType) as any}
										/>
									</div>
								);
							})}
						</div>
					</div>
				)}
				{this.shouldShowDynamicProducts && (
					<div className="container">
						<div className="row">
							<FavoriteList items={dynamicProducts as any} />
						</div>
					</div>
				)}
			</div>
		);
	}
}

export default withVirtualPageView(FavoritesView);
